import { useTranslation } from "react-i18next";
import { DashboardTile } from "@idwal/idwal-react-components";
import { SubgradeMetrics } from "../../types/Metric";
import { useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { sendGAEvent } from "../../AnalyticsService";
import { 
    DASHBOARD_CATEGORY, 
    DASHBOARD_AVERAGE_CREW_WELFARE_KPI_CLICK,
    DASHBOARD_MOST_SUCCESSFUL_AREA_KPI_CLICK,
    DASHBOARD_LEAST_SUCCESSFUL_AREA_KPI_CLICK
} from "../../constants/analytics";
import { SUBGRADES } from "../../types/RouteNames";

const POSITIVE_COLOR = "#20710D";
const NEGATIVE_COLOR = "#B90000";

type Props = {
    metrics: SubgradeMetrics | undefined;
    loading: boolean;
};

export const SubgradeTiles = (props: Props) => {
    const { metrics, loading } = props;

    const { t } = useTranslation("locale");
    const navigate = useNavigate();

    const elementsWithColourBar = useMemo(
        () => ({
            link: true,
            tooltip: false,
            colourBar: true,
            trend: false,
            label: true,
            button: false
        }),
        []
    );

    const elementsWithoutColourBar = useMemo(
        () => ({
            link: true,
            tooltip: false,
            colourBar: false,
            trend: false,
            label: true,
            button: false
        }),
        []
    );

    const onAverageCrewWelfareClick = useCallback(() => {
        sendGAEvent(DASHBOARD_CATEGORY, DASHBOARD_AVERAGE_CREW_WELFARE_KPI_CLICK);
        navigate(`/${SUBGRADES}`);
    }, [navigate]);

    const onMostSuccessfulAreaClick = useCallback(() => {
        sendGAEvent(DASHBOARD_CATEGORY, DASHBOARD_MOST_SUCCESSFUL_AREA_KPI_CLICK);
        navigate(`/${SUBGRADES}`);
    }, [navigate]);

    const onLeastSuccessfulAreaClick = useCallback(() => {
        sendGAEvent(DASHBOARD_CATEGORY, DASHBOARD_LEAST_SUCCESSFUL_AREA_KPI_CLICK);
        navigate(`/${SUBGRADES}`);
    }, [navigate]);

    const tiles = useMemo(
        () => {
            const averageCrewWelfareDeviation = metrics?.averageCrewWelfare?.deviation ?? 0;
            const averageCrewWelfareColor = averageCrewWelfareDeviation >= 0 ? POSITIVE_COLOR : NEGATIVE_COLOR;

            const mostSuccessfulDeviation = metrics?.mostSuccessfulArea?.deviation ?? 0;
            const mostSuccessfulColor = mostSuccessfulDeviation >= 0 ? POSITIVE_COLOR : NEGATIVE_COLOR;

            const leastSuccessfulDeviation = metrics?.leastSuccessfulArea?.deviation ?? 0;
            const leastSuccessfulColor = leastSuccessfulDeviation >= 0 ? POSITIVE_COLOR : NEGATIVE_COLOR;

            return [
                {
                    metric: t("dashboard.averageCrewWelfare"),
                    value: metrics?.averageCrewWelfare?.average?.toFixed(0),
                    unit: `${t("dashboard.range")}: ${metrics?.averageCrewWelfare?.rangeMin}-${metrics?.averageCrewWelfare?.rangeMax}`,
                    label: <><span style={{color: averageCrewWelfareColor}}>{averageCrewWelfareDeviation?.toFixed(2)}%</span> {t("dashboard.vsMarketAverage")}</>,
                    onClick: onAverageCrewWelfareClick,
                    elements: elementsWithColourBar
                },
                {
                    metric: t("dashboard.mostSuccessfulArea"),
                    value: `${mostSuccessfulDeviation >= 0 ? '+' : ''}${mostSuccessfulDeviation?.toFixed(2)}%`,
                    label: metrics?.mostSuccessfulArea?.area,
                    unit: t("dashboard.aboveMarketAverage"),
                    onClick: onMostSuccessfulAreaClick,
                    elements: elementsWithoutColourBar,
                    valueColor: mostSuccessfulColor
                },
                {
                    metric: t("dashboard.leastSuccessfulArea"),
                    value: `${leastSuccessfulDeviation >= 0 ? '+' : ''}${leastSuccessfulDeviation?.toFixed(2)}%`,
                    label: metrics?.leastSuccessfulArea?.area,
                    unit: t("dashboard.belowMarketAverage"),
                    onClick: onLeastSuccessfulAreaClick,
                    elements: elementsWithoutColourBar,
                    valueColor: leastSuccessfulColor
                },
            ];
        },
        [metrics, t, onAverageCrewWelfareClick, onMostSuccessfulAreaClick, onLeastSuccessfulAreaClick, elementsWithColourBar, elementsWithoutColourBar]
    );

    if (!metrics && !loading) {
        return (
            <div className="flex justify-content-center">
                <p>{t("dashboard.noDashboardAvailable")}</p>
            </div>
        );
    }

    return (
        <>
            {tiles.map((tile, index) => (
                <div key={index} className="col-12 md:col-4">
                    <DashboardTile
                        loading={loading}
                        data={tile}
                        elements={tile.elements}
                    />
                </div>
            ))}
        </>
    );
};
